// One domain: its DNS records, and what the console changed in them.
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import {
  useCloudDomainQuery,
  useDeleteRecordMutation,
  useSaveRecordMutation,
} from '@/client/api/cloud/cloud.query';
import { DnsRecord, DnsRecordWrite } from '@/client/types/cloud';
import {
  AccessDenied,
  AuditCard,
  CLOUD_BASE,
  Card,
  ConsoleLink,
  EmptyRow,
  ErrorNotice,
  FetchingChip,
  FormDialog,
  FormField,
  INPUT_CLASS,
  LoadingNote,
  PageShell,
  Th,
} from './cloudUi';

type Draft = { type: string; name: string; data: string; ttl: string; priority: string; port: string; weight: string };

const blank = (type = 'A'): Draft => ({
  type,
  name: '',
  data: '',
  ttl: '1800',
  priority: '10',
  port: '',
  weight: '',
});

const draftOf = (r: DnsRecord): Draft => ({
  type: r.type,
  name: r.name,
  data: r.data,
  ttl: String(r.ttl),
  priority: r.priority == null ? '' : String(r.priority),
  port: r.port == null ? '' : String(r.port),
  weight: r.weight == null ? '' : String(r.weight),
});

const DATA_HINT: Record<string, string> = {
  A: 'An IPv4 address, such as 157.230.19.40.',
  AAAA: 'An IPv6 address.',
  CNAME: 'The host this name points to. End a full name with a dot, or use @ for this domain.',
  MX: 'The mail server. End a full name with a dot.',
  TXT: 'The text, on one line.',
  SRV: 'The target host. End a full name with a dot.',
  CAA: 'The certificate authority, such as letsencrypt.org.',
};

const toWrite = (d: Draft): DnsRecordWrite => ({
  type: d.type,
  name: d.name.trim(),
  data: d.data.trim(),
  ttl: Number(d.ttl),
  ...(d.type === 'MX' || d.type === 'SRV' ? { priority: Number(d.priority) } : {}),
  ...(d.type === 'SRV' ? { port: Number(d.port), weight: Number(d.weight) } : {}),
});

const CloudDomainPage: React.FC = () => {
  const { name = '' } = useParams<{ name: string }>();
  const viewPerm = useHasPermission('cloud.view');
  const managePerm = useHasPermission('cloud.manage');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;
  const q = useCloudDomainQuery(name, canView);
  const d = q.data;
  useBreadcrumbs([
    { label: 'Cloud', href: CLOUD_BASE },
    { label: 'Resources', href: `${CLOUD_BASE}/resources` },
    { label: name },
  ]);

  const [editing, setEditing] = useState<{ record?: DnsRecord; draft: Draft } | null>(null);
  const [deleting, setDeleting] = useState<DnsRecord | null>(null);
  const [filter, setFilter] = useState('');
  const save = useSaveRecordMutation();
  const remove = useDeleteRecordMutation();
  const close = () => {
    setEditing(null);
    setDeleting(null);
    save.reset();
    remove.reset();
  };
  const set = (patch: Partial<Draft>) =>
    setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...patch } } : e));

  if (viewPerm.isLoading || (q.isLoading && !d)) {
    return (
      <PageShell title="Cloud" description="">
        <LoadingNote>Reading this domain&apos;s records.</LoadingNote>
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title="Cloud" description="">
        <AccessDenied />
      </PageShell>
    );
  }
  if (!d) {
    return (
      <PageShell title="Cloud" description="">
        <ErrorNotice error={q.error} onRetry={() => q.refetch()} />
      </PageShell>
    );
  }

  const needle = filter.trim().toLowerCase();
  const rows = d.records.filter(
    (r) =>
      !needle ||
      r.name.toLowerCase().includes(needle) ||
      r.data.toLowerCase().includes(needle) ||
      r.type.toLowerCase() === needle,
  );
  const draft = editing?.draft;
  const full = (host: string) => (host === '@' ? name : `${host}.${name}`);
  return (
    <PageShell
      title={name}
      description="DNS records for this domain. A change takes effect as each record's TTL runs out."
      actions={
        <div className="flex items-center gap-3">
          <FetchingChip />
          <ConsoleLink resource={{ kind: 'domain', id: name, name }} />
          {canManage && (
            <Button size="sm" onClick={() => setEditing({ draft: blank(d.types[0]) })}>
              <Plus className="mr-1 h-4 w-4" />
              Add record
            </Button>
          )}
        </div>
      }
    >
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      <Card
        title={`Records (${rows.length})`}
        action={
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name, value or type"
            aria-label="Filter records"
            className={`${INPUT_CLASS} h-8 w-64`}
          />
        }
        flush
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                <Th>Type</Th>
                <Th>Name</Th>
                <Th>Value</Th>
                <Th right>TTL</Th>
                <Th>{null}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow cols={5}>No records match</EmptyRow>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 align-top last:border-0">
                  <td className="px-4 py-2">
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-700">
                      {r.type}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-900">
                    {r.name}
                    <span className="block text-xs text-gray-500">{full(r.name)}</span>
                  </td>
                  <td className="max-w-xl break-all px-4 py-2 font-mono text-xs text-gray-700">
                    {r.priority != null && r.type === 'MX' ? `${r.priority} ` : ''}
                    {r.data}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-600">{r.ttl}s</td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    {r.locked ? (
                      <span
                        className="text-xs text-gray-400"
                        title="How the zone itself resolves; changed at the provider"
                      >
                        not editable here
                      </span>
                    ) : (
                      canManage && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditing({ record: r, draft: draftOf(r) })}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600"
                            onClick={() => setDeleting(r)}
                          >
                            Delete
                          </Button>
                        </>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <AuditCard rows={d.audit} empty="No record of this domain has been changed from the console" />

      {editing && draft && (
        <FormDialog
          title={editing.record ? `Change ${full(editing.record.name)}` : `Add a record to ${name}`}
          description={
            editing.record
              ? 'Whatever looks this name up gets the new value once the old TTL runs out.'
              : undefined
          }
          confirmLabel={editing.record ? 'Save' : 'Add record'}
          confirmText={editing.record ? name : undefined}
          valid={!!draft.name.trim() && !!draft.data.trim()}
          busy={save.isPending}
          error={save.error}
          onClose={close}
          onConfirm={(reason) =>
            save.mutate(
              { domain: name, recordId: editing.record?.id, record: toWrite(draft), reason },
              { onSuccess: close },
            )
          }
        >
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Type">
              <select
                value={draft.type}
                disabled={!!editing.record}
                onChange={(e) => set({ type: e.target.value })}
                className={`${INPUT_CLASS} mt-1 w-full`}
              >
                {d.types.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </FormField>
            <div className="col-span-2">
              <FormField label="Name" hint={`@ for ${name} itself, * for a wildcard.`}>
                <input
                  value={draft.name}
                  onChange={(e) => set({ name: e.target.value })}
                  className={`${INPUT_CLASS} mt-1 w-full font-mono`}
                />
              </FormField>
            </div>
          </div>
          <FormField label="Value" hint={DATA_HINT[draft.type]}>
            <input
              value={draft.data}
              onChange={(e) => set({ data: e.target.value })}
              className={`${INPUT_CLASS} mt-1 w-full font-mono`}
            />
          </FormField>
          <div className="grid grid-cols-4 gap-3">
            <FormField label="TTL (seconds)">
              <input
                type="number"
                min={30}
                max={86400}
                value={draft.ttl}
                onChange={(e) => set({ ttl: e.target.value })}
                className={`${INPUT_CLASS} mt-1 w-full`}
              />
            </FormField>
            {(draft.type === 'MX' || draft.type === 'SRV') && (
              <FormField label="Priority">
                <input
                  type="number"
                  min={0}
                  value={draft.priority}
                  onChange={(e) => set({ priority: e.target.value })}
                  className={`${INPUT_CLASS} mt-1 w-full`}
                />
              </FormField>
            )}
            {draft.type === 'SRV' && (
              <>
                <FormField label="Port">
                  <input
                    type="number"
                    min={0}
                    value={draft.port}
                    onChange={(e) => set({ port: e.target.value })}
                    className={`${INPUT_CLASS} mt-1 w-full`}
                  />
                </FormField>
                <FormField label="Weight">
                  <input
                    type="number"
                    min={0}
                    value={draft.weight}
                    onChange={(e) => set({ weight: e.target.value })}
                    className={`${INPUT_CLASS} mt-1 w-full`}
                  />
                </FormField>
              </>
            )}
          </div>
        </FormDialog>
      )}
      {deleting && (
        <FormDialog
          title={`Delete ${deleting.type} ${full(deleting.name)}`}
          description={`It points at ${deleting.data}. Once deleted, the name stops resolving as soon as its TTL (${deleting.ttl}s) runs out, and whatever depends on it stops being reachable by that name.`}
          confirmLabel="Delete record"
          destructive
          confirmText={name}
          busy={remove.isPending}
          error={remove.error}
          onClose={close}
          onConfirm={(reason) =>
            remove.mutate({ domain: name, recordId: deleting.id, reason }, { onSuccess: close })
          }
        />
      )}
    </PageShell>
  );
};

export default CloudDomainPage;
